/**
 * StudioModes — the front door.
 *
 * Studio used to drop everyone straight into a paragraph form with a waveform
 * button, and the honest feedback was that nobody could tell what the screen
 * was for. You now say what you want to make today, and the editor shows only
 * the panels that matter for it.
 *
 * HONESTY RULE: every card here does what it says TODAY. "Photo → Video" adds
 * real camera motion, captions and a voice — it does not claim AI animation,
 * which needs a video provider we haven't wired yet. If you add a card, the
 * flow behind it must already work.
 */
import * as L from "lucide-react";

export const MODES = [
  {
    id: "audio",
    icon: L.AudioLines,
    kicker: "Text → Audio",
    title: "Narrate something",
    line: "Paste a script or have Rammy write one. Pick voices, add a music bed that ducks under the words, mark chapters — out comes a finished MP3.",
    gets: ["MP3 + per-paragraph stems", "Chapters & timestamps", "10-language dubs"],
    cta: "Start narrating",
  },
  {
    id: "video",
    icon: L.Clapperboard,
    kicker: "Text → Video",
    title: "Make a video from a script",
    line: "Each line becomes a scene with its own picture — generated, or your own. Name a Cast member and the same character shows up in every scene.",
    gets: ["9:16, 1:1 or 16:9 MP4", "Captions burned in", "Your Cast, kept consistent"],
    cta: "Start a video",
    featured: true,
  },
  {
    id: "photo",
    icon: L.Images,
    kicker: "Photos → Video",
    title: "Turn your photos into a video",
    line: "Drop in your own photos. Rammy can genuinely animate them, lay your voice over the top and burn in the captions.",
    gets: ["Real motion, or a camera move", "Your voice over the top", "Ready for Reels & Shorts"],
    cta: "Use my photos",
  },
  {
    id: "clip",
    icon: L.Sparkles,
    kicker: "Prompt → Video",
    title: "Describe a shot",
    line: "One line of description, and Sora makes the footage. Good for the establishing shot you can't film — drop it straight into a scene.",
    gets: ["4, 8 or 12 seconds", "Vertical or wide", "Lands in your Creations"],
    cta: "Describe a shot",
  },
];

export function StudioModes({ onPick, projectCount = 0, onBrowse }) {
  return (
    <div className="max-w-5xl mx-auto p-8 md:p-10" data-testid="studio-modes">
      <div className="max-w-2xl">
        <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-[var(--rammy-cyan-dark)]">
          Studio
        </div>
        <h1 className="font-display text-4xl sm:text-5xl mt-3 leading-[1.05]">
          What are you making today?
        </h1>
        <p className="mt-4 text-base text-[var(--rammy-text-sec)] leading-relaxed">
          Describe it and Rammy decides how to build it — or pick a starting point
          yourself and you'll only see the controls that matter for it.
        </p>
      </div>

      <button
        onClick={() => onPick("brief")}
        data-testid="studio-mode-brief"
        className="group mt-8 w-full text-left rounded-3xl p-6 md:p-7 border border-[var(--rm-ink-900)] bg-[var(--rm-ink-900)] text-white transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_20px_60px_rgba(17,19,24,0.32)]"
      >
        <div className="flex items-start gap-4">
          <div className="w-11 h-11 rounded-2xl bg-[var(--rammy-cyan)] text-[var(--rm-ink-900)] flex items-center justify-center shrink-0">
            <L.Wand2 size={19} strokeWidth={1.9} />
          </div>
          <div className="min-w-0">
            <span className="text-[10px] uppercase tracking-[0.18em] font-semibold text-white/45">
              Describe it → Rammy builds it
            </span>
            <div className="font-display text-2xl mt-2">Just say what you want.</div>
            <p className="mt-2 text-[13.5px] text-white/65 leading-relaxed max-w-xl">
              "Three product photos of my candle and a 10-second clip for Reels." Rammy
              works out whether that's pictures, footage or a voiceover, picks the engine
              for each piece, and shows you the price before it spends anything.
            </p>
            <div className="mt-4 inline-flex items-center gap-1.5 text-[13px] font-medium text-[var(--rammy-cyan)]">
              Write a brief
              <L.ArrowRight size={14} className="group-hover:translate-x-0.5 transition-transform" />
            </div>
          </div>
        </div>
      </button>

      <div className="mt-8 text-[10px] uppercase tracking-[0.18em] font-semibold text-[var(--rammy-text-muted)]">
        or drive it yourself
      </div>

      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
        {MODES.map((m) => {
          const Icon = m.icon;
          return (
            <button
              key={m.id}
              onClick={() => onPick(m.id)}
              data-testid={`studio-mode-${m.id}`}
              className={`group text-left rounded-3xl p-6 border transition-all duration-300 hover:-translate-y-0.5 ${
                m.featured
                  ? "bg-[var(--rammy-cyan)]/10 border-[var(--rammy-cyan)]/40 hover:shadow-[0_18px_50px_rgba(72,224,200,0.18)]"
                  : "bg-[var(--rm-surface)] border-[var(--rammy-border)] hover:border-[var(--rammy-cyan)] hover:shadow-[0_14px_40px_rgba(17,19,24,0.06)]"
              }`}
            >
              <div className="flex items-start justify-between gap-3">
                <div
                  className={`w-11 h-11 rounded-2xl flex items-center justify-center ${
                    m.featured
                      ? "bg-[var(--rammy-cyan-dark)] text-white"
                      : "bg-[var(--rammy-cyan)]/15 text-[var(--rammy-cyan-dark)]"
                  }`}
                >
                  <Icon size={19} strokeWidth={1.8} />
                </div>
                <span className="text-[10px] uppercase tracking-[0.18em] font-semibold text-[var(--rammy-text-muted)] pt-1.5">
                  {m.kicker}
                </span>
              </div>

              <div className="font-display text-xl mt-4">{m.title}</div>
              <p className="mt-2 text-[13.5px] text-[var(--rammy-text-sec)] leading-relaxed">
                {m.line}
              </p>

              <ul className="mt-4 space-y-1">
                {m.gets.map((g) => (
                  <li key={g} className="flex items-center gap-1.5 text-[12px] text-[var(--rammy-text-muted)]">
                    <L.Check size={12} className="text-[var(--rammy-cyan-dark)] shrink-0" />
                    {g}
                  </li>
                ))}
              </ul>

              <div className="mt-5 inline-flex items-center gap-1.5 text-[13px] font-medium text-[var(--rammy-text)] group-hover:text-[var(--rammy-cyan-dark)] transition-colors">
                {m.cta}
                <L.ArrowRight size={14} className="group-hover:translate-x-0.5 transition-transform" />
              </div>
            </button>
          );
        })}
      </div>

      {projectCount > 0 && (
        <button
          onClick={onBrowse}
          className="mt-8 text-[13px] text-[var(--rammy-text-muted)] hover:text-[var(--rammy-text)] inline-flex items-center gap-1.5 transition-colors"
          data-testid="studio-modes-browse"
        >
          <L.FolderOpen size={14} />
          or open one of your {projectCount} project{projectCount === 1 ? "" : "s"}
        </button>
      )}
    </div>
  );
}

export default StudioModes;
